import { Routes, Route, Navigate, useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useAuthStore } from './stores/auth';
import { userApi, groupApi } from './api/client';

// 布局组件
import { MainLayout } from './components/layout/MainLayout';
import { AuthLayout } from './components/layout/AuthLayout';

// 页面组件
import { HomePage } from './pages/HomePage';
import { LoginPage } from './pages/LoginPage';
import { RegisterPage } from './pages/RegisterPage';
import { DashboardPage } from './pages/DashboardPage';
import { GroupListPage } from './pages/groups/GroupListPage';
import { GroupPage } from './pages/groups/GroupPage';
import { RepoPage } from './pages/repos/RepoPage';
import { RepoRouter } from './components/RepoRouter';
import { RepoLayout } from './components/layout/RepoLayout';
import { AdminDashboardPage } from './pages/admin/AdminDashboard';
import { UserSettingsPage } from './pages/settings/UserSettingsPage';
import { DocCanonicalRedirect } from './docs/DocCanonicalRedirect';
import { DocPage } from './docs/DocPage';


function App() {
    const { isAuthenticated } = useAuthStore();

    return (
        <Routes>
            {/* 公开页面 */}
            <Route path="/" element={<MainLayout />}>
                <Route index element={isAuthenticated ? <Navigate to="/dashboard" /> : <HomePage />} />
                <Route path="explore" element={<GroupListPage />} />
            </Route>

            {/* 认证页面 */}
            <Route element={<AuthLayout />}>
                <Route path="login" element={<LoginPage />} />
                <Route path="register" element={<RegisterPage />} />
            </Route>

            {/* 需要登录的页面 */}
            <Route element={<MainLayout requireAuth />}>
                <Route path="dashboard" element={<DashboardPage />} />
                <Route path="new/project" element={<div>新建项目</div>} />
                <Route path="new/group" element={<div>新建群组</div>} />
                <Route path="settings/*" element={<UserSettingsPage />} />
                <Route path="admin" element={<AdminDashboardPage />} />
            </Route>

            {/* 文档（须在 :owner/:repo 之前，避免 docs/foo 被当成仓库路径） */}
            <Route path="docs" element={<MainLayout />}>
                <Route path="zh/:slug" element={<DocPage lang="zh" />} />
                <Route path="en/:slug" element={<DocPage lang="en" />} />
                <Route path=":slug" element={<DocCanonicalRedirect />} />
            </Route>

            {/* 群组页面 - 支持任意层级嵌套 */}
            <Route path="groups" element={<MainLayout />}>
                <Route index element={<GroupListPage />} />
                {/* 使用 :topLevel/* + :topLevel 的组合以确保优先级高于 :owner/:repo */}
                <Route path=":topLevel/*" element={<GroupPage />} />
                <Route path=":topLevel" element={<GroupPage />} />
            </Route>

            {/* 仓库页面（统一 RepoLayout 壳 + Tab） */}
            <Route path=":owner/:repo" element={<MainLayout />}>
                <Route element={<RepoLayout />}>
                    <Route index element={<RepoPage />} />
                    <Route path="-/*" element={<RepoRouter fromLayout />} />
                </Route>
            </Route>

            {/* 用户或群组主页 */}
            <Route path=":owner" element={<MainLayout />}>
                <Route index element={<OwnerPage />} />
            </Route>



            {/* 任意深度的仓库路径 (fallback) */}
            <Route path="*" element={<RepoRouter />} />
        </Routes>
    );
}

// 用户或群组主页：根据 owner 调 API 区分用户与群组
function OwnerPage() {
    const { owner } = useParams<{ owner: string }>();
    const [type, setType] = useState<'user' | 'group' | 'loading' | 'notfound'>('loading');

    useEffect(() => {
        if (!owner) {
            setType('notfound');
            return;
        }
        (async () => {
            try {
                await userApi.getByUsername(owner);
                setType('user');
            } catch {
                try {
                    await groupApi.get(owner);
                    setType('group');
                } catch {
                    setType('notfound');
                }
            }
        })();
    }, [owner]);

    if (type === 'loading') {
        return (
            <div style={{ padding: '2rem', textAlign: 'center' }}>
                <p>加载中…</p>
            </div>
        );
    }
    if (type === 'notfound') {
        return (
            <div style={{ padding: '2rem', textAlign: 'center' }}>
                <h2>未找到</h2>
                <p>用户或群组 @{owner} 不存在</p>
            </div>
        );
    }
    if (type === 'group') {
        return <Navigate to={`/groups/${owner}`} replace />;
    }
    return (
        <div style={{ padding: '2rem', textAlign: 'center' }}>
            <h2>@{owner}</h2>
            <p>用户主页</p>
        </div>
    );
}

export default App;
